import React, { useEffect, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { api } from "../../../api/apiClient";
import config from "../../../config";
import { ServiceInstanceImageDto } from "../../types/types";
import { notify } from "../../components-ui/toast";

interface Props {
  serviceInstanceId: number;
}

const MAX_IMAGES = 5;

export default function ServiceInstanceImagesCard({ serviceInstanceId }: Props) {
  const [images, setImages] = useState<ServiceInstanceImageDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [permissionConfirmed, setPermissionConfirmed] = useState(false);

  const loadImages = async () => {
    setLoading(true);
    try {
      setImages(
        await api<ServiceInstanceImageDto[]>(
          `/api/serviceinstances/${serviceInstanceId}/images`,
        ),
      );
    } catch {
      notify.error("Failed to load session images.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPermissionConfirmed(false);
    loadImages();
    // loadImages intentionally refreshes whenever the selected instance changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceInstanceId]);

  const uploadImages = async (selectedFiles: FileList | null) => {
    if (!selectedFiles?.length) return;

    const remaining = MAX_IMAGES - images.length;
    const files = Array.from(selectedFiles).slice(0, remaining);
    if (selectedFiles.length > remaining) {
      notify.info(`Only ${remaining} more image${remaining === 1 ? "" : "s"} can be added.`);
    }

    setUploading(true);
    try {
      for (const file of files) {
        const data = new FormData();
        data.append("file", file);
        await api<ServiceInstanceImageDto>(
          `/api/serviceinstances/${serviceInstanceId}/images`,
          { method: "POST", body: data },
        );
      }
      notify.success("Session images uploaded.");
      await loadImages();
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (image: ServiceInstanceImageDto) => {
    if (!window.confirm("Remove this session image?")) return;
    await api(`/api/serviceinstances/images/${image.serviceInstanceImageId}`, {
      method: "DELETE",
    });
    setImages((current) =>
      current.filter(
        (item) => item.serviceInstanceImageId !== image.serviceInstanceImageId,
      ),
    );
    notify.success("Session image removed.");
  };

  const remaining = MAX_IMAGES - images.length;

  return (
    <section className="session-images-card">
      <div className="session-images-header">
        <div>
          <h3>Session images</h3>
          <p>{images.length} of {MAX_IMAGES} images added</p>
        </div>
        <label
          className={`action-button compact session-image-upload ${
            !permissionConfirmed || remaining === 0 || uploading ? "disabled" : ""
          }`}
        >
          <PhotoCameraIcon fontSize="small" />
          {uploading ? "Uploading…" : "Add images"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            disabled={!permissionConfirmed || remaining === 0 || uploading}
            onChange={(event) => {
              void uploadImages(event.target.files);
              event.target.value = "";
            }}
          />
        </label>
      </div>

      <label className="session-image-permission option-item">
        <input
          type="checkbox"
          checked={permissionConfirmed}
          onChange={(event) => setPermissionConfirmed(event.target.checked)}
        />
        I confirm that permission has been obtained to store these images.
      </label>

      {loading && <div className="field-hint">Loading session images…</div>}
      {!loading && images.length === 0 && (
        <div className="session-images-empty">No images have been added yet.</div>
      )}

      {images.length > 0 && (
        <div className="session-images-grid">
          {images.map((image) => {
            const imageUrl = `${config.API_BASE_URL}${image.url}`;
            return (
              <article key={image.serviceInstanceImageId} className="session-image-item">
                <a href={imageUrl} target="_blank" rel="noreferrer">
                  <img src={imageUrl} alt={image.originalFileName || "Session"} />
                </a>
                <button
                  type="button"
                  className="icon-button"
                  title="Remove image"
                  aria-label="Remove image"
                  onClick={() => deleteImage(image)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </button>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
